/**
 * Lightweight i18n system using JSON files and React context.
 *
 * Pattern matches the Flutter app's LocalizationManager:
 * - Dot-notation key lookup: t('nav.articles')
 * - Parameter substitution: t('article_card.words', { count: 500 })
 * - Fallback to English if key missing in current language
 * - Language change triggers re-render via context
 *
 * English (en.json) is the source of truth and bundled statically.
 * Other language files live in langs/ and are loaded on demand via
 * dynamic import (Vite code-splits them into separate chunks).
 */

import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import type { FC, ReactNode } from 'react';
import enStrings from './en.json';

type StringMap = Record<string, any>;

function resolve(obj: StringMap, path: string): string | undefined {
  const parts = path.split('.');
  let current: any = obj;
  for (const part of parts) {
    if (current == null || typeof current !== 'object') return undefined;
    current = current[part];
  }
  return typeof current === 'string' ? current : undefined;
}

function interpolate(template: string, params: Record<string, string | number>): string {
  return template.replace(/\{(\w+)\}/g, (_, key) => {
    return key in params ? String(params[key]) : `{${key}}`;
  });
}

interface I18nContextType {
  t: (key: string, params?: Record<string, string | number>) => string;
  language: string;
  setLanguage: (code: string) => void;
}

const I18nContext = createContext<I18nContextType | undefined>(undefined);

export const useTranslation = () => {
  const context = useContext(I18nContext);
  if (!context) {
    throw new Error('useTranslation must be used within an I18nProvider');
  }
  return context;
};

// Cache loaded language modules to avoid re-importing
const languageCache: Record<string, StringMap> = { en: enStrings };

async function loadLanguage(code: string): Promise<StringMap> {
  if (languageCache[code]) return languageCache[code];
  try {
    const module = await import(`./langs/${code}.json`);
    languageCache[code] = module.default;
    return module.default;
  } catch (err) {
    console.error(`[i18n] Failed to load ${code}.json, falling back to English`, err);
    return enStrings;
  }
}

export const I18nProvider: FC<{ children: ReactNode; initialLanguage?: string }> = ({
  children,
  initialLanguage = 'en',
}) => {
  const [language, setLanguageState] = useState(initialLanguage);
  const [strings, setStrings] = useState<StringMap>(enStrings);

  useEffect(() => {
    if (language === 'en') {
      setStrings(enStrings);
      return;
    }
    loadLanguage(language).then(setStrings);
  }, [language]);

  const setLanguage = useCallback((code: string) => {
    setLanguageState(code);
  }, []);

  const t = useCallback(
    (key: string, params?: Record<string, string | number>): string => {
      // Try current language, fall back to English
      const value = resolve(strings, key) ?? resolve(enStrings, key) ?? key;
      return params ? interpolate(value, params) : value;
    },
    [strings]
  );

  return (
    <I18nContext.Provider value={{ t, language, setLanguage }}>
      {children}
    </I18nContext.Provider>
  );
};
